<template>
	<div class="container-fluid p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div>
				<h1 class="mb-0">
					Station #{{ stationID }}
					<span
						v-if="detail"
						class="badge rounded-pill align-middle ms-2 fs-6"
						:class="detail.active ? 'bg-success' : 'bg-secondary'"
					>
						{{ detail.active ? "Active" : "Retired" }}
					</span>
				</h1>
				<p class="text-muted mb-0">
					One place the district traps from (§23): the site it stands on, where
					it was put, and every trap set there.
				</p>
			</div>
			<RouterLink
				to="/_/traps/stations"
				class="btn btn-outline-secondary btn-sm"
			>
				<i class="bi bi-arrow-left"></i> All stations
			</RouterLink>
		</div>

		<div v-if="loading" class="text-center py-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
			<p class="mt-2 text-muted">Loading station…</p>
		</div>

		<div v-else-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>

		<div v-else-if="detail" class="row g-4">
			<div class="col-12">
				<div class="card">
					<div
						class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2"
					>
						<span>Station</span>
						<div class="d-flex gap-2">
							<!-- Back to the map with this station selected: the map reads
							     `?station=` and frames it. -->
							<RouterLink
								:to="`/_/traps/stations?station=${detail.id}`"
								class="btn btn-outline-secondary btn-sm"
							>
								<i class="bi bi-map me-1"></i>Show on map
							</RouterLink>
							<ButtonLoading
								text="Retire"
								icon="bi bi-slash-circle"
								variant="outline-danger"
								size="sm"
								:loading="retiring"
								:disabled="!detail.active"
								@click="retire"
							/>
						</div>
					</div>

					<div class="card-body">
						<!-- §4.4's facts. An imported station states no creator and a
						     station whose site is not parcel-linked states no parcel —
						     both read `—` rather than an invented value. -->
						<dl class="row mb-0">
							<dt class="col-sm-4 text-muted">Created</dt>
							<dd class="col-sm-8">{{ formatInstant(detail.created) }}</dd>

							<dt class="col-sm-4 text-muted">Created by</dt>
							<dd class="col-sm-8">{{ detail.created_by_name || "—" }}</dd>

							<dt class="col-sm-4 text-muted">Site</dt>
							<dd class="col-sm-8">
								<RouterLink :to="siteRoute">{{ siteText }}</RouterLink>
								<div class="small text-muted">
									{{ detail.site_address || "—" }}
								</div>
								<div class="small text-muted">
									Kind: {{ detail.site_kind || "—" }}
								</div>
							</dd>

							<dt class="col-sm-4 text-muted">Parcel</dt>
							<dd class="col-sm-8">
								<template v-if="detail.parcel">
									<div>
										{{ detail.parcel.region }} {{ detail.parcel.external_id }}
									</div>
									<div class="small text-muted">
										{{ detail.parcel.property_type || "—" }}
									</div>
									<div class="small text-muted">
										Owner: {{ detail.parcel.owner || "—" }}
									</div>
								</template>
								<template v-else>—</template>
							</dd>

							<dt class="col-sm-4 text-muted">Location</dt>
							<dd class="col-sm-8">
								<template v-if="detail.location">
									<div>
										{{ detail.location.latitude }},
										{{ detail.location.longitude }}
									</div>
									<div class="small text-muted">
										Recorded {{ formatInstant(detail.location.recorded_at) }}
									</div>
								</template>
								<template v-else>—</template>
							</dd>

							<dt class="col-sm-4 text-muted">Permission</dt>
							<dd class="col-sm-8">
								<span
									class="badge rounded-pill"
									:class="
										detail.permission === 'permitted'
											? 'bg-success'
											: 'bg-warning text-dark'
									"
								>
									{{ PERMISSION_LABELS[detail.permission] }}
								</span>
							</dd>

							<dt class="col-sm-4 text-muted">Trap instances</dt>
							<dd class="col-sm-8">{{ detail.instances.length }}</dd>
						</dl>
					</div>
				</div>
			</div>

			<div class="col-12">
				<div class="card">
					<div class="card-header">Traps set here</div>
					<div class="card-body">
						<p v-if="detail.instances.length === 0" class="text-muted mb-0">
							No traps have been set at this station.
						</p>

						<div v-else class="table-responsive">
							<table class="table table-sm table-striped align-middle mb-0">
								<thead class="table-light">
									<tr>
										<th scope="col">Set</th>
										<th scope="col">Collected</th>
										<th scope="col">Trap type</th>
										<th scope="col">Lure</th>
										<th scope="col" class="text-end">Total</th>
										<th scope="col" class="text-end">Female</th>
										<th scope="col">Positivity</th>
										<th scope="col">State</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="instance in detail.instances" :key="instance.id">
										<td>
											<RouterLink :to="`/_/traps/${instance.id}`">
												{{ formatInstant(instance.date) }}
											</RouterLink>
										</td>
										<td>
											{{
												instance.collected_at
													? formatInstant(instance.collected_at)
													: "—"
											}}
										</td>
										<td>{{ instance.trap_type || "—" }}</td>
										<td>{{ instance.lure || "—" }}</td>
										<td class="text-end">
											{{
												instance.counts.state === "analyzed"
													? instance.counts.total
													: "—"
											}}
										</td>
										<td class="text-end">
											{{
												instance.counts.state === "analyzed"
													? instance.counts.female
													: "—"
											}}
										</td>
										<td>
											{{
												POSITIVITY_LABELS[instance.positivity] ??
												instance.positivity
											}}
										</td>
										<td>
											{{
												INSTANCE_STATE_LABELS[instance.state] ?? instance.state
											}}
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { RouterLink, useRoute } from "vue-router";

import { apiClient } from "@/client";
import {
	INSTANCE_STATE_LABELS,
	POSITIVITY_LABELS,
} from "@/components/map-review/columns";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { formatDate } from "@/format";
import { ROUTE_NAMES } from "@/route/name";
import type { TrapStationDetail } from "@/type/api";

const route = useRoute();
// The station is addressed by the id in the path — the same number the tile
// feature, the list row and the map selection all carry.
const stationID = Number(route.params.id);

const detail = ref<TrapStationDetail | null>(null);
const loading = ref(false);
const error = ref("");
const retiring = ref(false);

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

const PERMISSION_LABELS: Record<string, string> = {
	permitted: "Permitted",
	awaiting_permission: "Awaiting permission",
};

/** The Site's own page — the review surface's site detail, which is where a
 * site's facts are read. */
const siteRoute = computed(() => ({
	name: ROUTE_NAMES.SITE_DETAIL,
	params: { id: String(detail.value?.site_id ?? "") },
}));

const siteText = computed(() =>
	detail.value ? `Site #${detail.value.site_id}` : "Site",
);

function formatInstant(value: string): string {
	return formatDate(new Date(value));
}

// A station of another district, an id that does not exist and an id that is
// not a number all read the same way: the read is org-scoped through the site,
// so the server answers 404 for each.
async function load() {
	if (!Number.isFinite(stationID)) {
		error.value = "That is not a station address.";
		return;
	}
	loading.value = true;
	error.value = "";
	try {
		detail.value = await apiClient.JSONGet<TrapStationDetail>(
			`/api/trap-station/${stationID}`,
		);
	} catch (e: any) {
		if (e?.response?.status === 404) {
			error.value = `Station #${stationID} was not found in your district.`;
		} else {
			error.value =
				e?.response?.data?.message ||
				e?.message ||
				"Failed to load the station.";
		}
	} finally {
		loading.value = false;
	}
}

/** Retiring is a PATCH that flips `active` off; the station is never deleted
 * and stays on the map behind the retired toggle. */
async function retire() {
	if (!detail.value || !detail.value.active) return;
	const confirmed = confirm(
		`Retire station #${detail.value.id}? It stays on the map as a retired station, and its traps stay readable.`,
	);
	if (!confirmed) return;

	retiring.value = true;
	error.value = "";
	try {
		await apiClient.JSONPatch<{ id: number; active: boolean }>(
			`/api/trap-station/${detail.value.id}`,
			{},
		);
		detail.value = { ...detail.value, active: false };
		toastTitle.value = "Station retired";
		toastMessage.value = `Station #${detail.value.id} is retired.`;
		toastShow.value = true;
	} catch (e: any) {
		error.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to retire the station.";
	} finally {
		retiring.value = false;
	}
}

onMounted(load);
</script>

<style scoped>
.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}
</style>
